import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './App.css';
import {
  MapPin, MessageCircle, ArrowDown, Star, Cake,
  Gift, Shirt, Volume2, VolumeX, Calendar
} from 'lucide-react';
import { listaInvitados } from './data/invitados';
import patitoJuan from './image/patitojuan.png';
import fotoAcha from './image/acha.jpeg';
// 🎵 Reemplaza este archivo por la canción de "El Patito Juan" cuando la tengas:
//    coloca el mp3 en src/music/ y cambia la ruta de abajo.
import musicaFondo from './music/Biper y sus Amigos - El Patito Juan (Video oficial).mp3';

/* ===========================================================
   Datos de la fiesta — Cumpleaños de Acha 🐤
   Tema: El Patito Juan · 2 añitos
   =========================================================== */
const partyData = {
  name: "Arisa Hadasa",
  nickname: "Acha",
  age: 2,
  // Horario aún por confirmar; se muestra "Por confirmar".
  // El conteo regresivo usa esta fecha (mediodía como referencia).
  date: "2026-10-24T12:00:00",
  timePending: true,
  locationPending: true,
  locationName: "Salón por confirmar",
  locationAddress: "Muy pronto te compartiremos el lugar 🎈",
  locationUrl: "", // Pega aquí el link de Google Maps cuando lo tengas
  // 👇 Cambia estos números y nombres por los reales (formato: 52 + 10 dígitos)
  hosts: { mom: "Mamá", dad: "Papá" },
  phones: { mom: "529515324897", dad: "529511233759" },
  dressCode: "Colores alegres 🎨",
};

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const formatDayBadge = (dateStr) => {
  const d = new Date(dateStr);
  return { day: d.getDate(), monthYear: `${MESES[d.getMonth()]} ${d.getFullYear()}` };
};

/* Divisor con burbujas y patitos 🫧 */
const DuckDivider = ({ light = false }) => (
  <div className={`duck-divider ${light ? 'duck-divider--light' : ''}`}>
    <span className="dd-line" />
    <span className="dd-bubble" />
    <span className="dd-duck">🐤</span>
    <span className="dd-bubble" />
    <span className="dd-line" />
  </div>
);

const Countdown = ({ targetDate }) => {
  const calculateTimeLeft = () => {
    const difference = +new Date(targetDate) - +new Date();
    if (difference <= 0) return { dias: 0, horas: 0, min: 0, seg: 0 };
    return {
      dias: Math.floor(difference / (1000 * 60 * 60 * 24)),
      horas: Math.floor((difference / (1000 * 60 * 60)) % 24),
      min: Math.floor((difference / 1000 / 60) % 60),
      seg: Math.floor((difference / 1000) % 60),
    };
  };
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  useEffect(() => {
    const timer = setTimeout(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearTimeout(timer);
  });
  const timerComponents = Object.keys(timeLeft).map((interval) => {
    if (!timeLeft[interval] && timeLeft[interval] !== 0) return null;
    return (
      <div className="time-item" key={interval}>
        <span className="time-val">{timeLeft[interval]}</span>
        <span className="time-label">{interval}</span>
      </div>
    );
  });
  return (
    <div className="countdown-wrapper">
      {timerComponents.length ? timerComponents : <span className="countdown-today">¡Es hoy! 🎉</span>}
    </div>
  );
};

function Invitation() {
  const { id } = useParams();
  const [invitado, setInvitado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio(musicaFondo);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.5;
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setIsPlaying(false);
      });
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const toggleMusic = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const [autoScroll, setAutoScroll] = useState(true);
  useEffect(() => {
    if (loading || !autoScroll) return;
    const el = contentRef.current;
    const scrollInterval = setInterval(() => {
      if (el) {
        el.scrollBy(0, 1);
        if (el.scrollTop + el.clientHeight >= el.scrollHeight) setAutoScroll(false);
      } else {
        window.scrollBy(0, 1);
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight) setAutoScroll(false);
      }
    }, 40);
    return () => clearInterval(scrollInterval);
  }, [loading, autoScroll]);

  useEffect(() => {
    const stopScrolling = () => {
      if (autoScroll) setAutoScroll(false);
    };
    window.addEventListener("wheel", stopScrolling);
    window.addEventListener("touchmove", stopScrolling);
    window.addEventListener("keydown", stopScrolling);
    window.addEventListener("mousedown", stopScrolling);
    return () => {
      window.removeEventListener("wheel", stopScrolling);
      window.removeEventListener("touchmove", stopScrolling);
      window.removeEventListener("keydown", stopScrolling);
      window.removeEventListener("mousedown", stopScrolling);
    };
  }, [autoScroll]);

  // Revela cada sección con un fundido suave al entrar en pantalla.
  useEffect(() => {
    if (loading) return;
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]);

  const addToCalendar = () => {
    const title = encodeURIComponent(`Cumpleaños de ${partyData.nickname} 🐤 (${partyData.age} años)`);
    const details = encodeURIComponent(
      `¡El Patito Juan está de fiesta porque ${partyData.nickname} cumple ${partyData.age} añitos! ` +
      `Horario y salón por confirmar. 🎈`
    );
    const location = encodeURIComponent(partyData.locationPending ? "Por confirmar" : `${partyData.locationName}, ${partyData.locationAddress}`);
    // Evento de todo el día (horario por confirmar)
    const start = "20261024";
    const end = "20261025";
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}&ctz=America/Mexico_City`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    if (id && listaInvitados[id]) {
      setInvitado(listaInvitados[id]);
    }
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [id]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-duck">
            <img src={patitoJuan} alt="Patito Juan" className="loading-duck-img" />
            <span className="loading-bubble b1" />
            <span className="loading-bubble b2" />
            <span className="loading-bubble b3" />
          </div>
          <h2 className="loading-names">{partyData.nickname}</h2>
          <p className="loading-tagline">Preparando la fiesta del patito...</p>
        </div>
      </div>
    );
  }

  const welcomeMessage = invitado?.mensaje ||
    "Será un día lleno de risas, globos y muchos ¡cua, cua! Nos encantará que vengas a chapotear y celebrar este primer gran cumple.";

  const { day, monthYear } = formatDayBadge(partyData.date);

  return (
    <div className="layout">
      <div className="mobile-bg" />

      {/* Escena del patito sticky (desktop) / hero (móvil) */}
      <div className="side-image-container">
        <div className="hero-scene">
          <span className="cloud cloud-1" />
          <span className="cloud cloud-2" />
          <img src={patitoJuan} alt="Patito Juan" className="hero-duck-img" />
          <span className="water" />
        </div>
        <div className="overlay-mobile">
          <p className="mobile-eyebrow">Mi primera gran fiesta</p>
          <h1 className="mobile-title">{partyData.nickname}</h1>
        </div>
      </div>

      <div className="content-container" ref={contentRef}>

        {/* Header */}
        <section className="section header-section reveal">
          <div className="sparkles-container">
            <Star className="sparkle sparkle-1" size={18} />
            <Star className="sparkle sparkle-2" size={14} />
          </div>
          <p className="overline">¡Estás invitado a mi cumpleaños!</p>
          <h1 className="desktop-title">{partyData.nickname}</h1>
          <div className="age-badge">
            <Cake size={20} /> ¡Cumplo {partyData.age} añitos!
          </div>
          <p className="verse">
            El <strong>Patito Juan</strong> está de fiesta 🐤 y quiere celebrar
            conmigo este día tan especial.
          </p>
          <DuckDivider />
          <p className="scripture-quote">
            "Cua, cua, cua... <br />
            hoy nado feliz por la vida, <br />
            porque cumplo mis primeros añitos <br />
            rodeada de quienes más quiero."
            <span className="scripture-source">— {partyData.name} —</span>
          </p>
          <ArrowDown className="scroll-icon" size={20} />
        </section>

        {/* Fecha */}
        <section className="section date-section reveal">
          <div className="celebration-banner">
            <span className="floating-duck duck-left">🐤</span>
            <div className="date-badge">
              <span className="day-number">{day}</span>
              <span className="month-year">{monthYear}</span>
            </div>
            <span className="floating-duck duck-right">🐤</span>
          </div>
          <p className="time-text">🕐 Horario por confirmar</p>
          <Countdown targetDate={partyData.date} />
          <button onClick={addToCalendar} className="btn-outline btn-calendar">
            <Calendar size={16} /> Agregar al calendario
          </button>
        </section>

        {/* Banda decorativa de agua + patitos */}
        <div className="water-divider" aria-hidden="true">
          <span className="wd-duck">🐤</span>
          <span className="wd-duck">🫧</span>
          <span className="wd-duck">🐤</span>
          <span className="wd-duck">🎈</span>
          <span className="wd-duck">🐤</span>
        </div>

        {/* Saludo personalizado */}
        <section className="section welcome-msg-section reveal">
          <div className="acha-photo-frame">
            <img src={fotoAcha} alt={partyData.name} className="acha-photo" />
            <span className="acha-photo-badge">🐤 {partyData.age}</span>
          </div>
          <p className="acha-photo-caption">¡Feliz cumple, {partyData.nickname}!</p>
          {invitado && (
            <div className="ticket-card">
              <p className="ticket-label">Invitación especial para</p>
              <h2 className="ticket-name">{invitado.nombre}</h2>
            </div>
          )}
          <p className="welcome-text">"{welcomeMessage}"</p>
        </section>

        {/* Galería / tarjetas del patito */}
        <section className="section gallery-section reveal">
          <p className="overline">¡A festejar!</p>
          <div className="gallery-scroll-container">
            <div className="duck-card duck-card--yellow">
              <img src={patitoJuan} alt="Patito Juan" className="duck-card-img" />
              <span className="duck-card-caption">¡Cua cua!</span>
            </div>
            <div className="duck-card duck-card--sky">
              <span className="duck-card-emoji">🎂</span>
              <span className="duck-card-caption">{partyData.age} añitos</span>
            </div>
            <div className="duck-card duck-card--orange">
              <span className="duck-card-emoji">🎈</span>
              <span className="duck-card-caption">¡A jugar!</span>
            </div>
          </div>
        </section>

        {/* Ubicación y Dress code */}
        <section className="section location-section reveal">
          <div className="icon-badge">
            <MapPin size={22} />
          </div>
          <h3>¿Dónde será la fiesta?</h3>
          <p className="venue-name">{partyData.locationName}</p>
          <p className="venue-address">{partyData.locationAddress}</p>
          {partyData.locationUrl ? (
            <a href={partyData.locationUrl} target="_blank" rel="noreferrer" className="btn-outline">
              Ver mapa
            </a>
          ) : (
            <span className="btn-outline btn-outline--disabled">Muy pronto 🐤</span>
          )}
          <div className="dress-code-container">
            <div className="divider-small" />
            <div className="icon-badge">
              <Shirt size={22} />
            </div>
            <p className="dress-code-label">Código de vestimenta</p>
            <p className="dress-code-value">{partyData.dressCode}</p>
          </div>
        </section>

        {/* Regalo */}
        <section className="section gift-section reveal">
          <div className="icon-badge">
            <Gift size={22} />
          </div>
          <h3>Regalos</h3>
          <p className="gift-text">Tu presencia es el mejor regalo. 🐤</p>
          <p className="gift-text-small">
            Si deseas consentir a la festejada, <br />
            un detalle o un sobre serán recibidos con mucho cariño.
          </p>
        </section>

        {/* RSVP */}
        <section className="section rsvp-section reveal">
          <DuckDivider light />
          <h3>Confirma tu asistencia</h3>
          <div className="rsvp-intro-wrap">
            <p className="rsvp-intro">
              Estamos preparando cada detalle con mucho amor.<br />
              <strong>¡Nos encantará que vengas a festejar con nosotros!</strong>
            </p>
            <p className="rsvp-deadline">
              Por favor, ayúdanos confirmando tu asistencia antes del <strong>17 de Octubre</strong>.
            </p>
          </div>
          <div className="rsvp-buttons">
            <a
              href={`https://wa.me/${partyData.phones.mom}?text=${encodeURIComponent(
                `¡Hola! 👋 Soy ${invitado ? invitado.nombre : ''}.\n\n` +
                `Muchas gracias por invitarnos al cumple de ${partyData.nickname}. 🐤🎈\n` +
                `✅ Confirmo con gusto mi asistencia.\n\n` +
                `¡Ahí nos vemos para festejar!`
              )}`}
              className="btn-rsvp" target="_blank" rel="noreferrer"
            >
              <MessageCircle size={16} /> Confirmar con {partyData.hosts.mom}
            </a>
            <a
              href={`https://wa.me/${partyData.phones.dad}?text=${encodeURIComponent(
                `¡Hola! 👋 Soy ${invitado ? invitado.nombre : ''}.\n\n` +
                `Muchas gracias por invitarnos al cumple de ${partyData.nickname}. 🐤🎈\n` +
                `✅ Confirmo con gusto mi asistencia.\n\n` +
                `¡Ahí nos vemos para festejar!`
              )}`}
              className="btn-rsvp" target="_blank" rel="noreferrer"
            >
              <MessageCircle size={16} /> Confirmar con {partyData.hosts.dad}
            </a>
          </div>
          <p className="exclusive-note">¡Te esperamos con muchos globos! 🎈</p>
        </section>

        <footer className="footer-simple">
          <span className="footer-duck">🐤</span>
          <small>Con cariño, {partyData.name}.</small>
          <span className="footer-duck">🐤</span>
        </footer>
      </div>

      {/* Botón música */}
      <button
        onClick={toggleMusic}
        className="music-btn-fixed"
        aria-label={isPlaying ? 'Pausar música' : 'Reproducir música'}
      >
        {isPlaying ? (
          <span className="music-icon-wrap">
            <Volume2 size={22} />
            <span className="ripple" />
          </span>
        ) : (
          <VolumeX size={22} />
        )}
      </button>
    </div>
  );
}

export default Invitation;
